import type { Mercury } from '@repo/types/Models';
import orderMessageSegments from '../utils/orderMessageSegments';
import clsx from 'clsx';
import { useQuery } from '@apollo/client/react';
import { decodePaging } from '@repo/ui/Pagination';
import GET_SMS_REQUESTS_FOR_ACCOUNT_Q from '../gql/getSmsRequestsForAccount.gql';
import { useAccount } from '@/hooks/useAccount';
import { Skeleton } from '@repo/ui/Skeletons';

interface SmsWithSegmentsProps {
  primarySegment: Mercury.Models.SMS;
  inDialog?: boolean;
}

const SmsWithSegments = ({ primarySegment, inDialog }: SmsWithSegmentsProps) => {
  const account = useAccount();

  const PAGE_SIZE = 1000;

  const {
    loading: relatedSegmentsLoading,
    error: relatedSegmentsError,
    data: relatedSegmentsData,
  } = useQuery(GET_SMS_REQUESTS_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging('1', PAGE_SIZE),
      queryList: [
        {
          key: 'multipart_id',
          value: {
            value: primarySegment?.multipartId,
          },
        },
      ],
      id: account.id,
    },
    skip: !primarySegment?.multipartId,
    notifyOnNetworkStatusChange: true,
  });

  const numberOfSegments = relatedSegmentsData?.smsRequestsForAccount.edges.length;

  const orderedSegments = relatedSegmentsData
    ? orderMessageSegments(
        relatedSegmentsData?.smsRequestsForAccount.edges.map((edge) => edge.node)
      )
    : [];

  return (
    <div className="flex flex-col gap-2 ">
      {orderedSegments.length > 0 ? (
        orderedSegments.map((segment, index) => {
          if (relatedSegmentsLoading) {
            return <Skeleton count={1} key={index} height="80px" />;
          }

          const isPrimarySegment = segment.id === primarySegment.id;

          return (
            <Segment
              segment={segment}
              isPrimarySegment={isPrimarySegment}
              accountId={account.id}
              numberOfSegments={numberOfSegments}
              linkToSegment={inDialog ? true : isPrimarySegment ? false : true}
            />
          );
        })
      ) : (
        <Segment
          segment={primarySegment}
          isPrimarySegment={true}
          accountId={account.id}
          numberOfSegments={numberOfSegments}
          linkToSegment={inDialog ? true : false}
        />
      )}
    </div>
  );
};

export default SmsWithSegments;

interface SegmentProps {
  segment: Mercury.Models.SMS;
  isPrimarySegment: boolean;
  accountId: Mercury.Models.Account['id'];
  numberOfSegments?: number;
  linkToSegment?: boolean;
}

const Segment = ({
  segment,
  isPrimarySegment,
  accountId,
  numberOfSegments,
  linkToSegment,
}: SegmentProps) => {
  return (
    <div
      key={segment.id}
      className={clsx(
        '@container flex flex-col gap-2 p-2 border rounded-md border-grey-400',
        isPrimarySegment && 'bg-blue-200/50 dark:bg-blue-900/50'
      )}
    >
      <p className="">{segment.displayBody}</p>

      <div className="flex items-end justify-between">
        <p className="text-sm text-grey-500">
          ID: {segment.id}
          <br />
          Segment: {numberOfSegments ? `${segment.udhSeqNum}/${numberOfSegments}` : '1/1'}
        </p>

        {linkToSegment && (
          <a
            href={`/a/${accountId}/messaging/smses/${segment.id}`}
            className="flex items-center gap-1 ml-auto transition-colors text-grey-500 hover:text-grey-1000 "
          >
            <span className="material-symbols-outlined">link</span>
            <span>View Segment</span>
          </a>
        )}
      </div>
    </div>
  );
};

interface ConcatenatedMessageProps {
  primarySegment: Mercury.Models.SMS;
}

export const ConcatenatedMessage = ({ primarySegment }: ConcatenatedMessageProps) => {
  const account = useAccount();

  const PAGE_SIZE = 1000;

  const { loading, data } = useQuery(GET_SMS_REQUESTS_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging('1', PAGE_SIZE),
      queryList: [
        {
          key: 'multipart_id',
          value: {
            value: primarySegment?.multipartId,
          },
        },
      ],
      id: account.id,
    },
    skip: !primarySegment?.multipartId,
    notifyOnNetworkStatusChange: true,
  });

  const orderedSegments = data
    ? orderMessageSegments(data?.smsRequestsForAccount.edges.map((edge) => edge.node))
    : [];

  if (loading) {
    return <Skeleton count={1} height="80px" />;
  }

  // Single segment case - just show the message
  if (orderedSegments.length <= 1) {
    return (
      <div className="p-3 border rounded-md border-grey-400 bg-blue-200/50">
        <p>{primarySegment.displayBody}</p>
      </div>
    );
  }

  // Build concatenated message with highlighting
  return (
    <div className="p-3 border rounded-md border-grey-400">
      <p>
        {orderedSegments.map((segment, index) => {
          const isPrimary = segment.id === primarySegment.id;
          return (
            <span key={segment.id} className={clsx(isPrimary && 'bg-blue-200/50 rounded px-0.5')}>
              {segment.displayBody}
            </span>
          );
        })}
      </p>
    </div>
  );
};
